/**
 * **다가오는 적을 한 화면에.** 맞붙기 전에는 화면을 조금 물려(`APPROACH_ZOOM`까지) 장수와 적 대열이 함께 보이게 하고,
 * 적이 가까워지면 평소 배율로 돌아온다.
 *
 * 적이 횡대로 오게 된 뒤(Todo11) QA가 짚었다: 폰 세로 화면에서는 판을 열어도 적 대열의 양끝이 화면 밖이고 "적 72" 표지만
 * 보인다. 평소 배율(2배)에서 폰 세로의 화면 폭은 전장 195px인데 적의 선 폭은 400px 남짓이다. PC에서도 판을 연 순간에는
 * 적이 화면 위 밖에 있어 "횡대로 다가온다"는 것이 맞붙기 직전에야 보였다.
 *
 * 화면(렌더링)만의 일이다 — 시뮬레이션은 읽기만 한다. 그림(Pixi)을 모르는 셈만 둔다(시험이 본다).
 */
import { Team } from '../sim/units.ts';
import type { World } from '../sim/world.ts';

/** 전장 좌표(1/256픽셀)를 픽셀로. */
const PX = 256;

/** 다가오는 장면의 한 프레임: 배율, 보는 사람에서 가장 먼 쪽 적까지의 앞뒤 거리, 그 쪽(-1 위 · +1 아래). */
export interface ApproachScene {
  readonly zoom: number;
  readonly gap: number;
  readonly dir: number;
}

/**
 * 물리는 배율의 바닥. 처음에는 경로를 그릴 때의 배율(`DRAG_ZOOM` 1.25)과 같게 두었다. 그런데 보는 사람을 화면 안에 두면
 * (`APPROACH_KEEP`) 폰 세로에서는 그 배율로 적까지(650px 남짓)가 들어오지 않아, 판을 연 뒤 2.5초 동안 적이 한 명도 안
 * 보였다(QA: 맞붙기 전 화면 안의 적 31~58%) — 우리만 보이고 적은 표지뿐인, 고치기 전의 거울상이었다. 1배까지 물린다:
 * 사람이 작아지지만 판을 여는 몇 초뿐이고, 적이 다가오면 저절로 커진다. 넓은 화면은 여기까지 내려오지 않는다.
 */
export const APPROACH_ZOOM = 1;
/** 화면 가장자리와 대열 사이에 남기는 여유(전장 픽셀, 한쪽). */
export const APPROACH_PAD = 48;
/** 장수와 가장 가까운 적 사이가 이 안이면 다가오는 장면은 끝이다(전장 픽셀). 평소 배율로 돌아가 싸움을 본다. */
export const APPROACH_END = 260;
/**
 * 보는 사람(장수, 또는 몰고 있는 동료)을 제 쪽 화면 끝에서 이만큼 안에 둔다(화면 길이의 몫). 처음에는 장수와 적 대열의
 * 한가운데를 봤다 — 폰 세로에서 장수가 화면 맨 아래로 밀려 명령 줄 뒤에 들어갔다(QA: 깊이 10에서 맞붙기 전 249틱
 * 가운데 108틱). 적 대열은 보이는데 우리가 안 보였다. 0.3이면 폰 세로에서 장수가 조이스틱 위에 선다(0.24는 겹쳤다).
 */
export const APPROACH_KEEP = 0.3;
/** 장면이 끝난 뒤 평소 배율·평소 카메라로 돌아가는 걸음을 이 프레임에 걸쳐 올린다 — 첫 걸음이 갑작스러웠다(QA). */
export const APPROACH_EASE_FRAMES = 40;

/**
 * 다가오는 장면의 배율: 적 대열의 폭(`foeWidth`)과 장수에서 가장 먼 쪽 적까지의 앞뒤 거리(`gap`)가 화면에 들어오는 가장
 * 큰 배율. `base`(평소 배율)를 넘지 않고 `APPROACH_ZOOM`보다 작아지지 않는다 — 다 들어오지 않아도 그 아래로는 물리지
 * 않는다(사람이 점이 된다). `viewW`·`viewH`는 화면 픽셀을 전장의 가로·세로 쪽으로 읽은 값이다(가로 화면이면 뒤바뀐다).
 */
export function approachZoom(viewW: number, viewH: number, foeWidth: number, gap: number, base: number): number {
  const fitW = viewW / Math.max(1, foeWidth + APPROACH_PAD * 2);
  // 앞뒤로는 보는 사람 뒤에 `APPROACH_KEEP`만큼을 남긴 나머지에 적까지의 거리가 들어와야 한다.
  const fitH = (viewH * (1 - APPROACH_KEEP)) / Math.max(1, gap + APPROACH_PAD);
  return Math.max(Math.min(APPROACH_ZOOM, base), Math.min(base, fitW, fitH));
}

/**
 * 볼 곳을 보는 사람에게서 적 쪽으로 얼마나 옮기나(전장 픽셀). 적과의 한가운데까지 가되, 보는 사람이 제 쪽 화면 끝에서
 * `APPROACH_KEEP` 안으로 밀려나지 않는 데까지만이다. `viewLen`은 지금 배율에서 화면에 들어오는 앞뒤 길이.
 */
export function approachShift(gap: number, viewLen: number): number {
  return Math.max(0, Math.min(gap / 2, viewLen * (0.5 - APPROACH_KEEP)));
}

/**
 * 다가오는 장면의 상태. 한 판에 하나(렌더러가 쥔다) — 끝나면 다시 열리지 않는다: 싸우는 동안 화면이 숨 쉬듯 물러나면 안
 * 된다. 처음에는 렌더러 안에 있었는데 그림 없이는 시험할 수 없어, 리뷰가 권한 대로 여기로 옮겼다(보는 사람을 바꿀 때·
 * 끝날 때의 회귀를 시험이 본다).
 */
export class ApproachView {
  private done = false;
  /** 끝난 뒤 지난 프레임 수(`APPROACH_EASE_FRAMES`까지). */
  private eased = 0;

  /** 장면이 끝났는가. */
  get over(): boolean {
    return this.done;
  }

  /**
   * 이번 프레임의 장면. 끝났으면 null — 첫 피해가 났거나(화살 포함), 보는 사람(`u`)에게 가장 가까운 적이 `APPROACH_END`
   * 안에 들었거나, 적이 없다. 적이 다가올수록 앞뒤 거리가 줄어 배율이 저절로 평소 쪽으로 돌아온다.
   *
   * **보는 사람을 기준으로 잰다**(부대를 고르는 중이면 그 지휘관, 아니면 모는 사람, 아니면 장수 — 렌더러가 고른다).
   * 처음에는 늘 장수로 쟀는데, 동료를 골라 몰면 카메라가 장수와 적 사이에 남아 모는 동료가 화면 밖으로 나갔다(리뷰).
   * `sw`·`sh`는 화면 픽셀을 전장의 가로·세로 쪽으로 읽은 값(가로 화면이면 뒤바뀐다), `base`는 평소 배율.
   */
  scene(w: World, u: number, sw: number, sh: number, base: number): ApproachScene | null {
    if (this.done) return null;
    const ux = w.x[u] / PX;
    const uy = w.y[u] / PX;
    let minX = Infinity;
    let maxX = -Infinity;
    let farY = uy;
    let near2 = Infinity;
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] === 0 || w.team[i] !== Team.Enemy) continue;
      const x = w.x[i] / PX;
      const y = w.y[i] / PX;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (Math.abs(y - uy) > Math.abs(farY - uy)) farY = y;
      const d2 = (x - ux) * (x - ux) + (y - uy) * (y - uy);
      if (d2 < near2) near2 = d2;
    }
    if (near2 === Infinity || near2 < APPROACH_END * APPROACH_END || w.stats.damage[Team.Player] + w.stats.damage[Team.Enemy] > 0) {
      this.done = true;
      return null;
    }
    // 폭은 보는 사람을 가운데 둔 채 대열의 먼 끝까지 들어오게 잰다(옆은 보는 사람을 따르므로).
    const half = Math.max(Math.abs(minX - ux), Math.abs(maxX - ux));
    const gap = Math.abs(farY - uy);
    return { zoom: approachZoom(sw, sh, half * 2, gap, base), gap, dir: farY < uy ? -1 : 1 };
  }

  /**
   * 카메라 걸음의 몫(0~1). 장면 중에는 1, 끝난 뒤에는 프레임마다 1/`APPROACH_EASE_FRAMES`씩 올라 1에 닿는다 — 프레임마다
   * 한 번 부른다. 걸음이 제 빠르기에 닿는 데 걸리는 프레임이지, 배율이 평소 값에 닿는 때는 아니다.
   */
  ease(): number {
    if (!this.done) return 1;
    if (this.eased < APPROACH_EASE_FRAMES) this.eased++;
    return this.eased / APPROACH_EASE_FRAMES;
  }
}
